import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card } from '@atoms/index';
import {
  complianceService,
  type RestaurantApplicationFormData,
} from '@services/complianceService';
import {
  RestaurantApplicationFormFields,
  validateSingleApplicationFormField,
} from './RestaurantApplicationFormFields';
import {
  isApplicationFormValid,
  mergeRestaurantPrefill,
  validateApplicationForm,
  type ApplicationFormFieldErrors,
  type ApplicationFormFieldKey,
} from '@utils/applicationFormValidation';

interface RestaurantApplicationFormModalProps {
  isOpen: boolean;
  restaurantId: string;
  restaurantName: string;
  onClose: () => void;
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: unknown } })?.response?.data;
  if (response instanceof Blob) {
    return fallback;
  }
  const envelope = response as { error?: { message?: string }; message?: string } | undefined;
  return envelope?.error?.message ?? envelope?.message ?? fallback;
}

async function extractBlobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (response?.data instanceof Blob) {
    try {
      const text = await response.data.text();
      const parsed = JSON.parse(text) as { error?: { message?: string }; message?: string };
      return parsed.error?.message ?? parsed.message ?? fallback;
    } catch {
      return fallback;
    }
  }
  return extractApiErrorMessage(error, fallback);
}

export function RestaurantApplicationFormModal({
  isOpen,
  restaurantId,
  restaurantName,
  onClose,
}: RestaurantApplicationFormModalProps) {
  const [formData, setFormData] = useState<RestaurantApplicationFormData | null>(null);
  const [prefillData, setPrefillData] = useState<RestaurantApplicationFormData | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ApplicationFormFieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await complianceService.getApplicationForm(restaurantId);
      setFormData(data.formData);
      setPrefillData(data.prefill ?? data.formData);
      setFieldErrors({});
    } catch {
      setError('Form verileri yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (!isOpen) return;
    void loadForm();
  }, [isOpen, loadForm]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const isFormValid = useMemo(
    () => (formData ? isApplicationFormValid(formData) : false),
    [formData],
  );

  const handleFieldBlur = (key: ApplicationFormFieldKey) => {
    if (!formData) return;
    const message = validateSingleApplicationFormField(key, formData[key] ?? '');
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  };

  const handleApplyRestaurantData = () => {
    if (!formData || !prefillData) return;
    setFormData(mergeRestaurantPrefill(formData, prefillData, true));
    setFieldErrors({});
    setSuccess('Restoran kaydındaki bilgiler forma uygulandı.');
    setError(null);
  };

  const handleSave = async () => {
    if (!formData) return;
    const errors = validateApplicationForm(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Lütfen işaretli alanları düzeltin.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await complianceService.saveApplicationForm(restaurantId, formData);
      setSuccess('Taslak kaydedildi');
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Taslak kaydedilemedi'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!formData) return;
    const errors = validateApplicationForm(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('PDF oluşturmak için tüm zorunlu alanları doğru şekilde doldurun.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      await complianceService.saveApplicationForm(restaurantId, formData);
      const blob = await complianceService.generateApplicationFormPdf(restaurantId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lezzet-yolda-basvuru-${restaurantName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      setSuccess('PDF indirildi. İmzalı/kaselı halini Belgeler bölümünden yükleyebilirsiniz.');
    } catch (err) {
      setError(await extractBlobErrorMessage(err, 'PDF oluşturulamadı'));
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Kapat" />
      <Card className="relative z-[10000] w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-900">Restoran Başvuru Formu</h3>
          <p className="text-sm text-gray-500 mt-1">{restaurantName}</p>
          <p className="text-xs text-gray-400 mt-2">
            Boş alanlar restoran kaydından otomatik doldurulur. Kayıtlı bilgileri yeniden
            uygulamak için aşağıdaki butonu kullanın.
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3 mb-4">{error}</div>
          )}
          {success && (
            <div className="rounded-xl bg-green-50 text-green-700 text-sm px-4 py-3 mb-4">
              {success}
            </div>
          )}

          {isLoading || !formData ? (
            <p className="text-gray-500 text-sm">Yükleniyor...</p>
          ) : (
            <>
              <div className="mb-4">
                <Button variant="outline" size="sm" onClick={handleApplyRestaurantData}>
                  Restoran Kaydından Doldur
                </Button>
              </div>
              <RestaurantApplicationFormFields
                formData={formData}
                fieldErrors={fieldErrors}
                onChange={setFormData}
                onFieldBlur={handleFieldBlur}
              />
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex flex-wrap gap-3 justify-end">
          <Button variant="secondary" onClick={onClose}>
            Kapat
          </Button>
          <Button variant="outline" onClick={handleSave} isLoading={isSaving} disabled={!formData}>
            Taslak Kaydet
          </Button>
          <Button
            onClick={handleGeneratePdf}
            isLoading={isGenerating}
            disabled={!formData || !isFormValid}
            title={!isFormValid ? 'PDF için tüm alanları doğru doldurun' : undefined}
          >
            PDF İndir
          </Button>
        </div>
      </Card>
    </div>
  );
}
